'use client';

import Link from 'next/link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Navigation from '../../../components/Navigation';
import RelatedPages from '../../../components/RelatedPages';
import { GuideEyebrow, GuideSection, GuideSubtitle, GuideTitle } from '../../../components/GuideHeading';

const Code = ({ children }: { children: string }) => (
    <Box
        component="pre"
        sx={{
            backgroundColor: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            p: 2,
            mb: 3,
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            lineHeight: 1.6,
        }}
    >
        {children}
    </Box>
);

export default function JsonPatchInPythonGuide() {
    return (
        <>
            <Navigation activePage="docs" />
            <main>
                <Container maxWidth="lg" className="main-container">
                    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
                        <GuideEyebrow>Guide</GuideEyebrow>
                        <GuideTitle>JSON Patch in Python</GuideTitle>
                        <GuideSubtitle>
                            How to apply and generate RFC 6902 JSON Patches in Python with the jsonpatch library - with working code you can copy straight into a project.
                        </GuideSubtitle>

                        <GuideSection>Installing the library</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            The de-facto standard library is <code>python-jsonpatch</code>. It implements every RFC 6902 operation - add, remove, replace, move, copy and test:
                        </Typography>
                        <Code>{`pip install jsonpatch`}</Code>

                        <GuideSection>Applying a JSON Patch</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            <code>jsonpatch.apply_patch</code> takes a document and a patch (both plain Python dicts and lists) and returns the patched document:
                        </Typography>
                        <Code>{`import jsonpatch

doc = {
    "name": "demo",
    "version": 1,
    "tags": ["a", "b"],
}

patch = [
    {"op": "replace", "path": "/version", "value": 2},
    {"op": "add", "path": "/tags/-", "value": "c"},
    {"op": "remove", "path": "/name"},
]

result = jsonpatch.apply_patch(doc, patch)
print(result)
# {'version': 2, 'tags': ['a', 'b', 'c']}`}</Code>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 3 }}>
                            Note the special path <code>/tags/-</code>: the dash appends to the end of an array. Paths follow JSON Pointer (RFC 6901), so <code>~</code> and <code>/</code> inside keys are escaped as <code>~0</code> and <code>~1</code>.
                        </Typography>

                        <GuideSection>Generating a patch from two documents</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            <code>jsonpatch.make_patch</code> diffs two documents and returns the operations that turn the first into the second - the same thing the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> does in your browser:
                        </Typography>
                        <Code>{`import jsonpatch

before = {"a": 1, "b": {"c": 2}}
after = {"a": 1, "b": {"c": 3, "d": 4}}

patch = jsonpatch.make_patch(before, after)
print(list(patch))
# [{'op': 'add', 'path': '/b/d', 'value': 4},
#  {'op': 'replace', 'path': '/b/c', 'value': 3}]`}</Code>

                        <GuideSection>Validating with the test operation</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            The test operation stops evaluation if a value does not match. Use it as a precondition guard so patches only apply to the state you expect:
                        </Typography>
                        <Code>{`patch = [
    {"op": "test", "path": "/version", "value": 1},
    {"op": "replace", "path": "/version", "value": 2},
]

jsonpatch.apply_patch(doc, patch)  # raises JsonPatchConflict if /version is not 1`}</Code>

                        <GuideSection>Handling errors</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Two exception types cover most failures. Catch them explicitly and log the failing operation - evaluation stops at a failed operation; rollback of earlier mutations depends on the implementation:
                        </Typography>
                        <Code>{`import jsonpatch

try:
    result = jsonpatch.apply_patch(doc, patch)
except jsonpatch.JsonPatchConflict as e:
    # test operation failed, or replace/remove on a missing value
    print("conflict:", e)
except jsonpatch.JsonPointerException as e:
    # path does not exist (wrong key, index out of range)
    print("bad pointer:", e)`}</Code>

                        <GuideSection>Common pitfalls</GuideSection>
                        <Typography component="ul" sx={{ color: 'var(--fg-muted)', mb: 3, pl: 3 }}>
                            <li><strong>Evaluation stops on error.</strong> RFC 6902 does not guarantee rollback of earlier operations; catch failures and verify library behavior before assuming transaction-like semantics.</li>
                            <li><strong>Numbers are not strings.</strong> A test on <code>{'{"value": 1}'}</code> fails against <code>{'{"value": "1"}'}</code>. Type mismatches are the most common source of JsonPatchConflict.</li>
                            <li><strong>Array indices shift.</strong> Removing index 0 makes index 1 become index 0. Order remove operations from the highest index down, or use value-based test guards.</li>
                            <li><strong>apply_patch does not mutate by default.</strong> It returns a new document; assign the result.</li>
                        </Typography>

                        <GuideSection>Try it in the browser first</GuideSection>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 2 }}>
                            Before writing Python, prototype the patch visually. Paste your two documents into the{' '}
                            <Link href="/" style={{ color: 'var(--accent)' }}>JSON Patch Generator</Link> to get the exact RFC 6902 operations, or use the{' '}
                            <Link href="/diff" style={{ color: 'var(--accent)' }}>JSON Diff tool</Link> to see every changed line. Then drop the generated patch into <code>apply_patch</code>.
                        </Typography>
                        <Typography sx={{ color: 'var(--fg-muted)', mb: 4 }}>
                            New to the format? Read the <Link href="/docs" style={{ color: 'var(--accent)' }}>JSON Patch documentation</Link> or browse{' '}
                            <Link href="/examples" style={{ color: 'var(--accent)' }}>real-world examples</Link>.
                        </Typography>

                        <RelatedPages currentPage="docs" />
                    </Box>
                </Container>
            </main>
        </>
    );
}
